import React, { useState } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Text,
  Grid,
  Progress,
  VStack,
  HStack,
  Badge,
  useDisclosure,
  useToast,
  Spinner,
  Divider,
  Icon,
  CircularProgress,
  CircularProgressLabel
} from '@chakra-ui/react';
import { Download, Filter, FileSpreadsheet, Eye, EyeOff, CheckCircle, Circle } from 'lucide-react';
import * as XLSX from 'xlsx';
import AppColors from '../../../constants/AppColors';
import auditSheetRepository from '../../../repositories/AuditSheetRepository';
import { decodeResponseValue } from '../../../utils/responseColumn';
import { useToastr } from '@laxmimanogna/code-quick-components';

const AuditSheetExport = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFields, setSelectedFields] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSelected, setShowSelected] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const toast = useToastr();

  const availableFields = [
    { key: 'encounter_no', label: 'Encounter Number' },
    { key: 'rendering', label: 'Rendering Provider' },
    { key: 'enc_dt', label: 'Encounter Date' },
    { key: 'billed_icd', label: 'Billed ICD' },
    { key: 'billed_cpt', label: 'Billed CPT' },
    { key: 'billed_modifier', label: 'Billed Modifier' },
    { key: 'provider_rvu', label: 'Provider RVU' },
    { key: 'provider_dollar_value', label: 'Provider Dollar Value' },
    { key: 'response', label: 'Response' },
    { key: 'cpt_agree', label: 'CPT Agree' },
    { key: 'cpt_disagree', label: 'CPT Disagree' },
    { key: 'icd_agree', label: 'ICD Agree' },
    { key: 'icd_disagree', label: 'ICD Disagree' },
    { key: 'audited_icd', label: 'Audited ICD' },
    { key: 'audited_cpt', label: 'Audited CPT' },
    { key: 'audited_modifier', label: 'Audited Modifier' },
    { key: 'audited_rvu', label: 'Audited RVU' },
    { key: 'audited_dollar_value', label: 'Audited Dollar Value' },
    { key: 'notes', label: 'Notes' },
    { key: 'provider_education', label: 'Provider Education' },
    { key: 'number_complexity', label: 'Number Complexity' },
    { key: 'amount_complexity', label: 'Amount Complexity' },
    { key: 'risk_complications', label: 'Risk Complications' },
  ];

  const advancedFilterOptions = [
    { key: 'only_cpt_agree', label: 'Only CPT Agree', conflicts: ['only_cpt_disagree'], disables: ['cpt_disagree'] },
    { key: 'only_cpt_disagree', label: 'Only CPT Disagree', conflicts: ['only_cpt_agree'], disables: ['cpt_agree'] },
  ];

  const filteredFields = availableFields.filter(field => {
    const matchesSearch = field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         field.key.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !showSelected || selectedFields.includes(field.key);
    return matchesSearch && matchesFilter;
  });

  const getDisabledFields = () => {
    const disabled = new Set();
    advancedFilters.forEach(filterKey => {
      const filter = advancedFilterOptions.find(f => f.key === filterKey);
      if (filter) {
        filter.disables.forEach(field => disabled.add(field));
      }
    });
    return disabled;
  };

  const isFieldDisabled = (fieldKey) => {
    return getDisabledFields().has(fieldKey);
  };

  const toggleField = (fieldKey) => {
    if (isFieldDisabled(fieldKey)) return;
    
    setSelectedFields(prev => 
      prev.includes(fieldKey) 
        ? prev.filter(key => key !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const toggleAdvancedFilter = (filterKey) => {
    const filter = advancedFilterOptions.find(f => f.key === filterKey);
    
    setAdvancedFilters(prev => {
      let newFilters;
      if (prev.includes(filterKey)) {
        newFilters = prev.filter(key => key !== filterKey);
      } else {
        newFilters = prev.filter(key => !filter.conflicts.includes(key));
        newFilters.push(filterKey);
      }
      return newFilters;
    });

    setSelectedFields(prev => {
      const newDisabled = new Set();
      const newAdvancedFilters = advancedFilters.includes(filterKey) 
        ? advancedFilters.filter(key => key !== filterKey)
        : [...advancedFilters.filter(key => !filter.conflicts.includes(key)), filterKey];
      
      newAdvancedFilters.forEach(filterKey => {
        const f = advancedFilterOptions.find(opt => opt.key === filterKey);
        if (f) {
          f.disables.forEach(field => newDisabled.add(field));
        }
      });

      return prev.filter(field => !newDisabled.has(field));
    });
  };

  const selectAll = () => {
    const disabledFields = getDisabledFields();
    const selectableFields = availableFields
      .filter(field => !disabledFields.has(field.key))
      .map(field => field.key);

    if (selectedFields.length === selectableFields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields(selectableFields);
    }
  };

  const fetchAuditData = async () => {
    try {
      setIsLoading(true);
      setDownloadProgress(0);
      setLoadingMessage('Preparing your request...');

      const params = new URLSearchParams();
      selectedFields.forEach(field => params.append('fields[]', field));
      advancedFilters.forEach(filter => params.append('filters[]', filter));

      setLoadingMessage('Connecting to server...');
      const response = await auditSheetRepository.getAuditSheetDataForExport(params.toString());

      // If the response is an error message instead of data array
      if (!Array.isArray(response)) {
        throw new Error(response?.message || 'Unexpected error occurred');
      }

      setLoadingMessage('Processing data...');
      return response;
      
    } catch (error) {
      console.error('Error fetching audit data:', error);
      throw error;
    } finally {
      setIsLoading(false);
      setDownloadProgress(0);
      setLoadingMessage('');
    }
  };


  // Function to convert JSON to Excel and download
  const downloadAsExcel = (data, filename = 'audit_export') => {
    try {
      setLoadingMessage('Creating Excel file...');
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Convert JSON to worksheet
      const ws = XLSX.utils.json_to_sheet(data);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Audit Data');
      
      // Generate Excel file and download
      XLSX.writeFile(wb, `${filename}.xlsx`);
      
      toast.showInfo({description: `Excel file with ${data.length} records has been downloaded successfully.`})
    } catch (error) {
      toast.showError({ description: `Failed to create Excel file: ${error}` });
    }
  };

  // Function to convert JSON to CSV and download
  const downloadAsCSV = (data, filename = 'audit_export') => {
    try {
      setLoadingMessage('Creating CSV file...');
      
      // Convert JSON to CSV
      const ws = XLSX.utils.json_to_sheet(data);
      const csv = XLSX.utils.sheet_to_csv(ws);
      
      // Create blob and download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.showInfo({description: `CSV file with ${data.length} records has been downloaded successfully.`});
    } catch (error) {
      toast.showError({ description: `Failed to create CSV file: ${error}` });
    }
  };

  // Main export handler
  const handleExport = async (format = 'excel') => {
    if (selectedFields.length === 0) {
      toast.showInfo({ description:'Please select at least one field to export.'});
      return;
    }

    try {
      // Fetch audit data and response options in parallel
      const [data, responseAuditIssues] = await Promise.all([
        fetchAuditData(),
        auditSheetRepository.getResponseAuditIssues(),
      ]);

      if (!data || !Array.isArray(data) || data.length === 0) {
        toast.showInfo({ description: 'No data available for the selected criteria.'});
        return;
      }

      // Decode response field before filtering
      const decodedData = data.map(row => {
        if (!row.response) return row;
        return { ...row, response: decodeResponseValue(row.response, row, responseAuditIssues) };
      });

      // Filter data to include only selected fields
      const filteredData = decodedData.map(row => {
        const filteredRow = {};

        selectedFields.forEach(prefix => {
          Object.keys(row).forEach(key => {
            if (key === prefix || key.startsWith(`${prefix}_`)) {
              filteredRow[key] = row[key];
            }
          });
        });

        return filteredRow;
      });

      // Download based on format
      if (format === 'csv') {
        downloadAsCSV(filteredData);
      } else {
        downloadAsExcel(filteredData);
      }

      onClose();
    } catch (error) {
      toast.showError({description: 'Failed to fetch or export data. Please try again.'})
    }
  };

  const selectableFieldsCount = availableFields.length - getDisabledFields().size;

  return (
    <Box>
      {/* Export Button */}
      <Flex justify="center">
        <Button
          onClick={onOpen}
          bg={AppColors.primary}
          color="white"
          size="lg"
          px={8}
          py={6}
          borderRadius="2xl"
          fontWeight="bold"
          fontSize="lg"
          boxShadow="2xl"
          _hover={{ bg: AppColors.secondary, transform: 'scale(1.05)' }}
          _active={{ transform: 'scale(0.95)' }}
          transition="all 0.3s ease"
          leftIcon={<Icon as={FileSpreadsheet} w={6} h={6} />}
        >
          Audit Data Export
        </Button>
      </Flex>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" closeOnOverlayClick={!isLoading}>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <ModalContent maxH="90vh" borderRadius="3xl">
          {/* Header */}
          <ModalHeader bg={AppColors.primary} color="white" borderTopRadius="3xl">
            <HStack>
              <Box
                w={12}
                h={12}
                bg="whiteAlpha.300"
                borderRadius="2xl"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={FileSpreadsheet} w={6} h={6} />
              </Box>
              <VStack align="start" spacing={0}>
                <Text fontSize="2xl" fontWeight="bold">Audit Data Export</Text>
                <Text fontSize="sm" color="whiteAlpha.800">Select fields to include in your export</Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" isDisabled={isLoading} />

          <ModalBody p={6}>
            {/* Loading Overlay */}
            {isLoading && (
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                bg="whiteAlpha.900"
                zIndex={1000}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="3xl"
              >
                <VStack spacing={6}>
                  <CircularProgress 
                    value={downloadProgress} 
                    color={AppColors.primary}
                    size="80px"
                    thickness="4px"
                  >
                    <CircularProgressLabel fontSize="sm" fontWeight="bold">
                      {downloadProgress}%
                    </CircularProgressLabel>
                  </CircularProgress>
                  <VStack spacing={2}>
                    <Text fontSize="lg" fontWeight="semibold" color={AppColors.secondary}>
                      {loadingMessage || 'Processing...'}
                    </Text>
                    <HStack>
                      <Spinner size="sm" color={AppColors.primary} />
                      <Text fontSize="sm" color={AppColors.darkGray}>
                        Please wait, this may take a few moments
                      </Text>
                    </HStack>
                  </VStack>
                </VStack>
              </Box>
            )}

            {/* Controls */}
            <VStack spacing={6}>
              <Flex w="full" gap={4} flexWrap="wrap">
                {/* Search */}
                <InputGroup flex={1} minW="250px">
                  <InputLeftElement>
                    <Icon as={Filter} color={AppColors.lightGrey} />
                  </InputLeftElement>
                  <Input
                    placeholder="Search fields..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    borderColor={AppColors.gray}
                    _focus={{ borderColor: AppColors.primary }}
                    borderRadius="xl"
                    isDisabled={isLoading}
                  />
                </InputGroup>

                {/* Toggle Buttons */}
                <HStack>
                  <Button
                    onClick={() => setShowSelected(!showSelected)}
                    variant={showSelected ? "solid" : "outline"}
                    bg={showSelected ? AppColors.primary : "transparent"}
                    color={showSelected ? "white" : AppColors.darkGray}
                    borderColor={showSelected ? AppColors.primary : AppColors.gray}
                    leftIcon={showSelected ? <Icon as={Eye} /> : <Icon as={EyeOff} />}
                    borderRadius="xl"
                    isDisabled={isLoading}
                  >
                    {showSelected ? 'Selected Only' : 'Show All'}
                  </Button>
                  
                  <Button
                    onClick={selectAll}
                    bg={AppColors.green}
                    color="white"
                    _hover={{ bg: AppColors.primary }}
                    leftIcon={selectedFields.length === selectableFieldsCount ? 
                      <Icon as={Circle} /> : <Icon as={CheckCircle} />}
                    borderRadius="xl"
                    isDisabled={isLoading}
                  >
                    {selectedFields.length === selectableFieldsCount ? 'Deselect All' : 'Select All'}
                  </Button>
                </HStack>
              </Flex>

              {/* Selection Summary */}
              <Box
                w="full"
                p={4}
                bg={AppColors.bgColor}
                borderRadius="xl"
                border="1px"
                borderColor={AppColors.primary}
              >
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color={AppColors.darkGray}>
                    Selected: <Text as="span" fontWeight="bold" color={AppColors.secondary}>
                      {selectedFields.length}
                    </Text> of {selectableFieldsCount} available fields
                  </Text>
                  <Progress
                    value={selectableFieldsCount > 0 ? (selectedFields.length / selectableFieldsCount) * 100 : 0}
                    w="130px"
                    colorScheme="teal"
                    borderRadius="full"
                  />
                </Flex>
              </Box>

              {/* Advanced Filters */}
              <Box w="full">
                <Text fontSize="lg" fontWeight="semibold" color={AppColors.secondary} mb={3}>
                  Advanced Filters
                </Text>
                <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={3}>
                  {advancedFilterOptions.map(filter => {
                    const isSelected = advancedFilters.includes(filter.key);
                    return (
                      <Button
                        key={filter.key}
                        onClick={() => toggleAdvancedFilter(filter.key)}
                        variant={isSelected ? "solid" : "outline"}
                        bg={isSelected ? AppColors.lightDanger : "white"}
                        color={isSelected ? AppColors.secondary : AppColors.darkGray}
                        borderColor={isSelected ? AppColors.danger : AppColors.gray}
                        justifyContent="flex-start"
                        p={3}
                        h="auto"
                        borderRadius="xl"
                        _hover={{ transform: 'scale(1.02)' }}
                        isDisabled={isLoading}
                      >
                        <HStack>
                          <Box
                            w={4}
                            h={4}
                            borderRadius="full"
                            border="2px"
                            borderColor={isSelected ? AppColors.danger : AppColors.lightGrey}
                            bg={isSelected ? AppColors.danger : "transparent"}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {isSelected && <Box w={2} h={2} bg="white" borderRadius="full" />}
                          </Box>
                          <Text fontSize="sm" fontWeight="medium">{filter.label}</Text>
                        </HStack>
                      </Button>
                    );
                  })}
                </Grid>
              </Box>

              <Divider />

              {/* Fields Grid */}
              <Box w="full" maxH="400px" overflowY="auto">
                <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={3}>
                  {filteredFields.map(field => {
                    const isSelected = selectedFields.includes(field.key);
                    const isDisabled = isFieldDisabled(field.key);
                    
                    return (
                      <Button
                        key={field.key}
                        onClick={() => toggleField(field.key)}
                        isDisabled={isDisabled || isLoading}
                        variant={isSelected && !isDisabled ? "solid" : "outline"}
                        bg={isSelected && !isDisabled ? AppColors.primary : (isDisabled ? AppColors.gray : "white")}
                        color={isSelected && !isDisabled ? "white" : (isDisabled ? AppColors.lightGrey : AppColors.darkGray)}
                        borderColor={isSelected && !isDisabled ? AppColors.primary : AppColors.gray}
                        justifyContent="flex-start"
                        p={4}
                        h="auto"
                        borderRadius="xl"
                        _hover={!isDisabled && !isLoading ? { transform: 'scale(1.02)' } : {}}
                        boxShadow={isSelected && !isDisabled ? "lg" : "none"}
                      >
                        <HStack>
                          <Box
                            w={5}
                            h={5}
                            borderRadius="full"
                            border="2px"
                            borderColor={isSelected && !isDisabled ? "white" : AppColors.darkGray}
                            bg={isSelected && !isDisabled ? "white" : "transparent"}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {isSelected && !isDisabled && 
                              <Icon as={CheckCircle} w={3} h={3} color={AppColors.primary} />}
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="semibold">{field.label}</Text>
                            <Text fontSize="xs" color="gray.500">{field.key}</Text>
                          </VStack>
                        </HStack>
                      </Button>
                    );
                  })}
                </Grid>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter bg={AppColors.bgColor} borderBottomRadius="3xl">
            <Flex justify="space-between" w="full" align="center">
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color={AppColors.darkGray}>
                  Ready to export <Text as="span" fontWeight="bold" color={AppColors.secondary}>
                    {selectedFields.length}
                  </Text> selected fields
                </Text>
                {advancedFilters.length > 0 && (
                  <Text fontSize="sm" color={AppColors.darkGray}>
                    with <Text as="span" fontWeight="bold" color={AppColors.danger}>
                      {advancedFilters.length}
                    </Text> advanced filters
                  </Text>
                )}
              </VStack>
              <HStack spacing={3}>
                <Button
                  onClick={onClose}
                  bg={AppColors.gray}
                  color={AppColors.darkGray}
                  borderRadius="xl"
                  isDisabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleExport('csv')}
                  isDisabled={selectedFields.length === 0 || isLoading}
                  isLoading={isLoading}
                  bg={AppColors.secondary}
                  color="white"
                  _hover={{ bg: AppColors.primary }}
                  leftIcon={!isLoading && <Icon as={Download} />}
                  borderRadius="xl"
                  loadingText="Exporting..."
                >
                  Export CSV
                </Button>
                <Button
                  onClick={() => handleExport('excel')}
                  isDisabled={selectedFields.length === 0 || isLoading}
                  isLoading={isLoading}
                  bg={AppColors.green}
                  color="white"
                  _hover={{ bg: AppColors.primary }}
                  leftIcon={!isLoading && <Icon as={Download} />}
                  borderRadius="xl"
                  loadingText="Exporting..."
                >
                  Export Excel
                </Button>
              </HStack>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AuditSheetExport;